import { getDuration } from '@common-fe/common-fe';
import axios from 'axios';
import MockAdapter from 'axios-mock-adapter';

import { RequestHeaders } from '@/common/constants';
import paths, { REFRESH_TOKEN } from '@/common/paths';
import { useStore as useOrphanModeStore } from '@/modules/core/store/useOrphanMode.store';
import CONFIG, { stopDataDogSession } from '@/utils/config';
import Token, { IS_SSO_TOKEN } from '@/utils/handlers/Token';
import { VERSION_LOCAL_STORAGE_KEY } from '@/utils/hooks/useVersionChecker';

import useStore from '../modules/user/stores/useAuth.store';

export interface RefreshBody {
  accessToken: string;
  refreshToken: string;
}

interface AxiosError {
  response: {
    status: number;
    message: string;
  };
  config: {
    url?: string;
    headers: {
      Authorization: string;
    };
  };
}

const VERSION_HEADER = 'X-B3-FE-Version';
export const BEARER = 'Bearer';
export const UNAUTHORIZED_STATUS_CODE = 401;

// const handleRefreshToken = async (
//   callback:(newToken: string) => void,
//   errorCallback: () => void,
// ) => {
//   try {
//     const currentSession = await Auth.currentSession();
//     const idToken = currentSession.getIdToken().getJwtToken();
//     await Token.storeToken(idToken);
//     if(!idToken) {
//       errorCallback();
//     }
//     callback(idToken);
//   } catch (e) {
//     errorCallback();
//   }
// };

const axiosInstance = axios.create({
  baseURL: CONFIG.BASE_URL,
});

axiosInstance.interceptors.request.use(async (request) => {
  request.headers[VERSION_HEADER] = localStorage.getItem(VERSION_LOCAL_STORAGE_KEY) || '';
  if (request?.url && request?.url === REFRESH_TOKEN) {
    return request;
  }
  request.headers.Authorization = Token.getToken() ? `${BEARER} ${Token.getToken() || ''}` : undefined;
  return request;
});

export const refreshToken = async () => {
  const refreshToken = Token.getRefreshToken();
  const userName = Token.getUserName();

  if (Token.getSSODataFromHash()) {
    return;
  }

  // if (!localStorage.getItem(IS_SSO_TOKEN) && !Token.isExpired()) {
  //   return;
  // }

  if (!refreshToken && localStorage.getItem(IS_SSO_TOKEN) && Token.isSsoExpired()) {
    throw new Error();
  }

  if (!refreshToken && localStorage.getItem(IS_SSO_TOKEN)) {
    return;
  }

  if (!refreshToken && !localStorage.getItem(IS_SSO_TOKEN)) {
    throw new Error();
  }

  const response = await axiosInstance.post(REFRESH_TOKEN, {
    user_name: userName,
    refresh_token: refreshToken,
  });

  const newAccessToken = response?.data?.access_token;
  const expiresIn = response?.data?.expires_in;

  if (!newAccessToken || !expiresIn) {
    throw new Error();
  }

  Token.storeToken(newAccessToken);
  Token.storeExpiresIn(expiresIn);
};

const forceRefreshToken = async () => {
  const { setAuth } = useStore.getState();
  try {
    await refreshToken();
  } catch {
    if (Token.isAuthenticated()) stopDataDogSession();
    Token.clear();
    setAuth(false);
  }
};

forceRefreshToken();
setInterval(
  () => {
    forceRefreshToken();
  },
  getDuration({ minutes: 4, seconds: 30 })
);

const getIsUnauthorizedException = (url?: string) => {
  if (!url) {
    return false;
  }

  return (
    url.includes(paths.CHANGE_PASSWORD) ||
    url.includes(paths.RESET_PASSWORD) ||
    url.includes(paths.SEND_FORGOT_PASSWORD_CODE)
  );
};

export const errorHandler = async (error: AxiosError) => {
  const { setAuth } = useStore.getState();

  const isUnauthorizedException = getIsUnauthorizedException(error?.config?.url);

  if (
    (error?.response?.status === UNAUTHORIZED_STATUS_CODE && !isUnauthorizedException) ||
    Token.isSsoExpired()
  ) {
    // TODO: test completely logout when token expired

    // await handleRefreshToken((newToken: string) => {
    //   error.config.headers.Authorization = `${BEARER} ${newToken}`;
    //   axios.request(error.config);
    // }, () => {
    //   Token.clear();
    //   setAuth(false);
    // });
    // Close the RUM session while the user is still attached to it (EL-30448).
    if (Token.isAuthenticated()) stopDataDogSession();
    Token.clear();
    setAuth(false);
  }

  return new Promise((resolve, reject) => {
    reject(error);
  });
};

const isSSOSignupUrl = (url?: string) => {
  if (!url) return false;

  const isSSOSignupUrl = new RegExp(/\/sso-signup/);

  return isSSOSignupUrl.test(url);
};
const isException = (url?: string) => {
  if (!url) return false;

  if (isSSOSignupUrl(url)) return true;

  return false;
};

// add header for orphan mode
axiosInstance.interceptors.request.use(async (request) => {
  if (isException(request.url)) return request;

  const { linkedEmployee } = useOrphanModeStore.getState();
  if (linkedEmployee && request.url !== paths.GET_LINKED_ACCOUNTS) {
    request.headers[RequestHeaders.preferredEmployeeId] = linkedEmployee.employeeId;
  }
  return request;
});

axiosInstance.interceptors.response.use((response) => response, errorHandler);

const mock = new MockAdapter(axiosInstance, { delayResponse: 250, onNoMatch: 'passthrough' });
import { AxiosResponse } from 'axios';

/**
 * Creates a mock AxiosResponse object for testing
 *
 * Usage:
 * vi.mocked(api.get).mockResolvedValue(mockAxiosResponse({ data: mockPayload }));
 */
function mockAxiosResponse<T>(partial: { data: T }): AxiosResponse<T> {
  return {
    data: partial.data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      headers: {} as any,
    },
  } as AxiosResponse<T>;
}

export { axiosInstance, mock, mockAxiosResponse };
